import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import type { Client } from '@/types/clients';

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null; // Si viene client, es modo edición
  onSubmit: (data: ClientFormData) => Promise<void>;
  loading?: boolean;
}

export interface ClientFormData {
  name: string;
  supabase_project_id: string;
  vault_secret_name: string;
  project_type: 'chatbot' | 'voicebot';
  status: 'active' | 'inactive' | 'maintenance';
  vapi_tag?: string;
}

export function ClientFormDialog({
  open,
  onOpenChange,
  client,
  onSubmit,
  loading = false
}: ClientFormDialogProps) {
  const isEditMode = !!client;

  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    supabase_project_id: '',
    vault_secret_name: '',
    project_type: 'chatbot',
    status: 'active',
    vapi_tag: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ClientFormData, string>>>({});

  // Initialize form with client data in edit mode
  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        supabase_project_id: client.supabase_project_id,
        vault_secret_name: client.vault_secret_name,
        project_type: client.project_type,
        status: client.status,
        vapi_tag: client.vapi_tag || ''
      });
    } else {
      // Reset form when creating new client
      setFormData({
        name: '',
        supabase_project_id: '',
        vault_secret_name: '',
        project_type: 'chatbot',
        status: 'active',
        vapi_tag: ''
      });
    }
    setErrors({});
  }, [client, open]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ClientFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.supabase_project_id.trim()) {
      newErrors.supabase_project_id = 'El Supabase Project ID es requerido';
    }

    if (!formData.vault_secret_name.trim()) {
      newErrors.vault_secret_name = 'El Vault Secret Name es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (field: keyof ClientFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Editar Cliente' : 'Crear Nuevo Cliente'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEditMode
              ? 'Actualiza la información del cliente. Los campos marcados con * son obligatorios.'
              : 'Completa la información para crear un nuevo cliente. Los campos marcados con * son obligatorios.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid gap-3 max-h-[60vh] overflow-y-auto pr-2">
            {/* Name */}
            <div className="space-y-1">
              <Label htmlFor="name" className="text-sm">Nombre del Cliente *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: Acme Corporation"
                disabled={loading}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Supabase Project ID */}
            <div className="space-y-1">
              <Label htmlFor="supabase_project_id" className="text-sm">Supabase Project ID *</Label>
              <Input
                id="supabase_project_id"
                value={formData.supabase_project_id}
                onChange={(e) => handleChange('supabase_project_id', e.target.value)}
                placeholder="Ej: abc123xyz"
                disabled={loading}
              />
              {errors.supabase_project_id && (
                <p className="text-xs text-red-500">{errors.supabase_project_id}</p>
              )}
            </div>

            {/* Vault Secret Name */}
            <div className="space-y-1">
              <Label htmlFor="vault_secret_name" className="text-sm">Vault Secret Name *</Label>
              <Input
                id="vault_secret_name"
                value={formData.vault_secret_name}
                onChange={(e) => handleChange('vault_secret_name', e.target.value)}
                placeholder="Ej: acme_corp_secret"
                disabled={loading}
              />
              {errors.vault_secret_name && (
                <p className="text-xs text-red-500">{errors.vault_secret_name}</p>
              )}
            </div>

            {/* Project Type */}
            <div className="space-y-1">
              <Label htmlFor="project_type" className="text-sm">Tipo de Proyecto *</Label>
              <Select
                value={formData.project_type}
                onValueChange={(value: 'chatbot' | 'voicebot') => handleChange('project_type', value)}
                disabled={loading}
              >
                <SelectTrigger id="project_type">
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chatbot">Chatbot 💬</SelectItem>
                  <SelectItem value="voicebot">Voicebot 📞</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label htmlFor="status" className="text-sm">Estado *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: 'active' | 'inactive' | 'maintenance') => handleChange('status', value)}
                disabled={loading}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Selecciona el estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                  <SelectItem value="maintenance">Mantenimiento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* VAPI Tag (optional) */}
            <div className="space-y-1">
              <Label htmlFor="vapi_tag" className="text-sm">VAPI Tag (opcional)</Label>
              <Input
                id="vapi_tag"
                value={formData.vapi_tag}
                onChange={(e) => handleChange('vapi_tag', e.target.value)}
                placeholder="Ej: acme-tag"
                disabled={loading}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {isEditMode ? 'Actualizando...' : 'Creando...'}
                </>
              ) : (
                <>{isEditMode ? 'Actualizar Cliente' : 'Crear Cliente'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
